import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Calendar } from '../../../components/icon/icon';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { subContractRegistrationSchema } from '../../../schema/workingSchema';
import { fetchDriverMypage } from '../../../api/mypage';
import { subContractRegistration } from '../../../api/working';
import { useHistory } from 'react-router-dom';
import { AlertModal } from '../../../components/modal/alert.modal';

interface DriverState {
  vehicle: string;
  phone: string;
  registration: string;
  to_user_id: string | null;
}

interface SubContractDto {
  target: string;
  content: string;
}

const CommonJobSearchRegist = () => {
  const today = new Date();
  const [profileData, setProfileData] = useState<DriverState | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<SubContractDto>({
    resolver: zodResolver(subContractRegistrationSchema),
  });
  const [targetDate, setTargetDate] = useState<Date>(today);

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/working',
      });
    }
  };

  // 폼 제출 처리 함수
  const onSubmit = async (data: SubContractDto) => {
    try {
      // 서버에 폼 데이터 전송
      const response = await subContractRegistration(data);

      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('일감 등록에 실패했습니다.');
        setIsModalOpen(true);
        setNavigateOnClose(false);
      }
    } catch (error) {
      console.error('일감 등록 중 오류가 발생했습니다.', error);
      setModalMessage('일감 등록 중 오류가 발생했습니다.');
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const driverData = await fetchDriverMypage();
        setProfileData(driverData);
      } catch (error) {
        console.error('데이터를 불러오는 데 실패했습니다.');
      }
    };

    fetchData();
  }, []);

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="car-info">
          <ul>
            <li>
              <div className="label">차량 번호</div>
              <div className="value">{profileData?.vehicle}</div>
            </li>
            <li>
              <div className="label">휴대폰 번호</div>
              <div className="value">{profileData?.phone}</div>
            </li>
            <li>
              <div className="label">도착 날짜</div>
              <div className="value">
                <DatePickerContainer>
                  <div className="inner">
                    <Controller
                      name="target"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <CustomDatePicker
                          className="search-input"
                          locale={ko}
                          dateFormat="yyyy-MM-dd"
                          selected={value ? new Date(value) : null} // value를 Date 타입으로 변환
                          startDate={targetDate}
                          selectsStart
                          onChange={(date: any) => onChange(date)}
                          placeholderText="날짜 선택"
                          withPortal
                        />
                      )}
                    />
                    <div className="icon-box">
                      <Calendar />
                    </div>
                  </div>
                </DatePickerContainer>
              </div>
            </li>
          </ul>
        </div>
        {errors.target && <ValTxt>{String(errors.target.message)}</ValTxt>}
        <div className="contents">
          <div className="title">내용</div>
          <div className="text-box">
            <textarea
              {...register('content')}
              placeholder="일감 구함에 등록할 내용을 입력해주세요."
            ></textarea>
          </div>
          {errors.content && (
            <ValTxt className="no-margin">
              {String(errors.content.message)}
            </ValTxt>
          )}
        </div>
        <Btn>등록 하기</Btn>
      </form>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default CommonJobSearchRegist;

// style
const Container = styled.div`
  height: 100%;
  padding: 0 2.4rem 9.4rem;
  form {
    padding: 2.4rem 0;
    height: 100%;
    .car-info {
      padding: 2.4rem 2.4rem 2.8rem;
      border-radius: 1.6rem;
      box-shadow: 0 2px 5px 3px rgba(0, 0, 0, 0.05);
      position: relative;
      ul {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        li {
          display: flex;
          gap: 2.4rem;
          align-items: center;
          .label {
            min-width: 5.6rem;
            font-size: 1.2rem;
            font-weight: 600;
            color: ${(props) => props.theme.textAAAColor};
            line-height: 1.6rem;
          }
          .value {
            font-size: 1.4rem;
            font-weight: 600;
            &.status {
              min-width: 4.2rem;
              color: ${(props) => props.theme.textWhiteColor};
              background-color: ${(props) => props.theme.btnBlueColor};
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0.4rem;
              font-size: 1.1rem;
              border-radius: 1.1rem;
            }
          }
        }
      }
    }
    .contents {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      margin-top: 2.6rem;
      height: calc(100% - 18rem);
      .title {
        font-size: 1.5rem;
        font-weight: 600;
      }
      .text-box {
        flex: 1;
        border-radius: 0.8rem;
        border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
        overflow: hidden;
        padding: 1.4rem 1.8rem;
        font-size: 1.4rem;
        font-weight: 500;
        textarea {
          width: 100%;
          height: 100%;
          flex: 1;
          border: none;
          &::placeholder {
            color: ${(props) => props.theme.searchPlaceholderColor};
            font-size: 1.4rem;
            font-weight: 00;
          }
        }
      }
    }
  }
`;

const Btn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const CustomDatePicker = styled(DatePicker)``;

const DatePickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  .inner {
    position: relative;
    width: 100%;
    display: flex;
    align-items: flex-end;
    .search-input {
      font-family: 'SUITE', 'sans-serif';
      width: 100%;
      font-size: 1.4rem;
      font-weight: 600;
      background-color: inherit;
      border: 0px;
      outline: none;
      padding: 0;
      max-width: 12rem;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 20rem;
      }
      .react-datepicker__time-container {
        width: 20rem;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item {
        width: 100%;
        height: 4.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.8rem;
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 0.1rem;
      transform: translateY(-50%);
      svg {
        path {
          fill: #d7dbde;
        }
      }
    }
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 1rem 1.7rem 0;
  &.no-margin {
    margin: 0rem 1.7rem;
  }
`;
